import { useState, useEffect, useCallback, useMemo } from 'react'
import { toast } from 'sonner'
import api from '@/services/api'
import type { Deliverable, Plan } from '@/components/Configuracao/plansTabTypes'
import {
  normalizePlan,
  getUnifiedDeliverables,
  calculateTotalHours,
  extractApiErrorMessage,
  buildPlanApiPayload,
  emptyPlan,
  resolvePlanTypeForCategory,
  supportPricingValue,
} from '@/components/Configuracao/plansTabUtils'
import { filterPlansByCatalogScope, type PlanCatalogScope } from '@/utils/planCatalog'

export function usePlansTab(options?: { catalogScope?: PlanCatalogScope }) {
  const catalogScope = options?.catalogScope
  const [plans, setPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(true)
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [modalTab, setModalTab] = useState<'info' | 'escopo'>('info')
  const [saving, setSaving] = useState(false)

  const fetchPlans = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/plans')
      const items = data.data ?? data ?? []
      const normalizedItems = items.map((item: Plan) => normalizePlan(item))
      setPlans(normalizedItems)
      localStorage.setItem('crm_plans', JSON.stringify(normalizedItems))
    } catch {
      const stored = localStorage.getItem('crm_plans')
      if (stored) {
        try {
          setPlans(JSON.parse(stored))
          toast.warning(
            'Não foi possível carregar planos da API. Exibindo cópia em cache do navegador — pode estar desatualizada em relação à VPS.',
            { duration: 8000 },
          )
        } catch {
          /* ignore */
        }
      } else {
        toast.error('Erro ao carregar planos. Verifique se a API local está online.')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  const visiblePlans = useMemo(() => {
    if (!catalogScope) return plans
    return filterPlansByCatalogScope(plans, catalogScope)
  }, [plans, catalogScope])

  useEffect(() => {
    fetchPlans()
  }, [fetchPlans])

  const closeModal = () => {
    setEditingPlan(null)
    setIsCreating(false)
  }

  const handleSavePlan = async () => {
    if (!editingPlan) return
    setSaving(true)
    try {
      const deliverables = getUnifiedDeliverables(editingPlan)
      const totalHours = calculateTotalHours(deliverables)
      const calculatedBaseValue = supportPricingValue(editingPlan.config)
      const suggestedValue = Number(editingPlan.config?.suggestedValue)
      const planType = resolvePlanTypeForCategory(editingPlan.category, editingPlan.type)
      const baseValueForSave =
        planType === 'DEMANDAS_ESPECIFICAS' && Number.isFinite(suggestedValue) && suggestedValue > 0
          ? suggestedValue
          : calculatedBaseValue

      const normalized = normalizePlan({
        ...editingPlan,
        type: resolvePlanTypeForCategory(editingPlan.category, editingPlan.type),
      })
      const payload = buildPlanApiPayload(normalized, deliverables, totalHours, baseValueForSave)
      if (catalogScope === 'rocket') {
        payload.category = 'Rocket'
        payload.config = { ...payload.config, catalogScope: 'rocket' }
      }

      if (payload.name.length < 2) {
        toast.error('Nome do plano deve ter pelo menos 2 caracteres')
        return
      }

      if (isCreating) {
        await api.post('/plans', payload)
        toast.success('Plano criado com sucesso!')
      } else {
        await api.patch(`/plans/${editingPlan.id}`, payload)
        toast.success('Plano atualizado com sucesso!')
      }

      closeModal()
      fetchPlans()
    } catch (err: unknown) {
      toast.error(`Erro ao salvar plano: ${extractApiErrorMessage(err)}`)
    } finally {
      setSaving(false)
    }
  }

  const handleDeletePlan = async (id: string) => {
    if (
      !window.confirm(
        'Certeza absoluta que deseja deletar este Plano (Matrix)? Isso não afeta negócios ganhos antigos, mas o remove do catálogo.',
      )
    )
      return
    try {
      await api.delete(`/plans/${id}`)
      toast.success('Plano deletado com sucesso!')
      fetchPlans()
    } catch (err: unknown) {
      toast.error(`Erro ao deletar: ${err instanceof Error ? err.message : 'Erro desconhecido'}`)
    }
  }

  const openNewPlanModal = () => {
    setIsCreating(true)
    setModalTab('info')
    setEditingPlan(emptyPlan(catalogScope === 'rocket' ? 'Rocket' : 'Marketing'))
  }

  const openEditModal = (plan: Plan) => {
    setIsCreating(false)
    setModalTab('info')
    setEditingPlan(plan)
  }

  const handleDeliverableChange = (idx: number, field: keyof Deliverable, value: string) => {
    if (!editingPlan) return
    const current = getUnifiedDeliverables(editingPlan)
    const updated = [...current]

    if (field === 'hours') {
      updated[idx] = { ...updated[idx], [field]: parseFloat(value) || 0 }
    } else {
      updated[idx] = { ...updated[idx], [field]: value }
    }

    setEditingPlan({
      ...editingPlan,
      config: { ...editingPlan.config, deliverables: updated },
    })
  }

  const addDeliverable = () => {
    if (!editingPlan) return
    const current = getUnifiedDeliverables(editingPlan)
    setEditingPlan({
      ...editingPlan,
      config: {
        ...editingPlan.config,
        deliverables: [...current, { name: '', category: '', quantity: '1', hours: 0 }],
      },
    })
  }

  const removeDeliverable = (idx: number) => {
    if (!editingPlan) return
    const current = getUnifiedDeliverables(editingPlan)
    const updated = [...current]
    updated.splice(idx, 1)
    setEditingPlan({
      ...editingPlan,
      config: { ...editingPlan.config, deliverables: updated },
    })
  }

  const modalDeliverables = editingPlan ? getUnifiedDeliverables(editingPlan) : []
  const modalTotalHours = calculateTotalHours(modalDeliverables)
  const modalCalculatedValue = supportPricingValue(editingPlan?.config)

  return {
    plans: visiblePlans,
    loading,
    editingPlan,
    isCreating,
    modalTab,
    saving,
    fetchPlans,
    closeModal,
    handleSavePlan,
    handleDeletePlan,
    openNewPlanModal,
    openEditModal,
    setModalTab,
    setEditingPlan,
    handleDeliverableChange,
    addDeliverable,
    removeDeliverable,
    modalDeliverables,
    modalTotalHours,
    modalCalculatedValue,
  }
}
